import { useState } from 'react'

import { useGetMonthlyAnalyticsQuery } from '@/store/analytic-api'
import { MonthlySummaryCard } from '@/pages/dashboard/components/monthly-summary-card'
import { DurationChartCard } from '@/pages/dashboard/components/duration-chart-card'
import { VolumeChartCard } from '@/pages/dashboard/components/volume-chart-card'
import {
  getMonthlySummaryStats,
  isAnalyticsPeriodResult,
  Period,
  PERIODS,
  shiftDate,
  useAnalyticsPeriodQuery,
} from '@/pages/dashboard/shared/analytics-utils'
import { BotStatusToggle } from '@/components/dashboard/bot-status-toggle'

const DashboardAnalytics = (): JSX.Element => {
  const today = new Date()
  const monthStartStr = new Date(today.getFullYear(), today.getMonth(), 1).toLocaleDateString('sv-SE')

  const { data: monthlyData } = useGetMonthlyAnalyticsQuery({ dateStart: monthStartStr })

  const [durationPeriod, setDurationPeriod] = useState<Period>(Period.Weekly)
  const [durationReferenceDate, setDurationReferenceDate] = useState<Date>(today)

  const [volumePeriod, setVolumePeriod] = useState<Period>(Period.Monthly)
  const [volumeReferenceDate, setVolumeReferenceDate] = useState<Date>(today)

  const { data: durationData, dateStart: durationDateStart } = useAnalyticsPeriodQuery(
    durationPeriod,
    durationReferenceDate,
  )
  const { data: volumeData, dateStart: volumeDateStart } = useAnalyticsPeriodQuery(
    volumePeriod,
    volumeReferenceDate,
  )
  const { totalMessages, totalThreads, averageThreadDuration, totalThreadDuration, averageThreadMessages } =
    getMonthlySummaryStats(monthlyData)

  function canShiftForward(date: Date): boolean {
    return date < today
  }

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      <div className="w-full lg:w-[400px] flex flex-col gap-6">
        <BotStatusToggle />
        {monthlyData && isAnalyticsPeriodResult(monthlyData) && (
          <MonthlySummaryCard
            from={monthlyData.from}
            totalMessages={totalMessages}
            totalThreads={totalThreads}
            averageThreadDuration={averageThreadDuration}
            totalThreadDuration={totalThreadDuration}
            averageThreadMessages={averageThreadMessages}
          />
        )}
      </div>

      <div className="flex-1 flex flex-col gap-6">
        <DurationChartCard
          period={durationPeriod}
          setPeriod={setDurationPeriod}
          referenceDate={durationReferenceDate}
          setReferenceDate={setDurationReferenceDate}
          data={durationData}
          dateStart={durationDateStart}
          canShiftForward={canShiftForward}
          shiftDate={shiftDate}
          periods={PERIODS}
        />

        <VolumeChartCard
          period={volumePeriod}
          setPeriod={setVolumePeriod}
          referenceDate={volumeReferenceDate}
          setReferenceDate={setVolumeReferenceDate}
          data={volumeData}
          dateStart={volumeDateStart}
          canShiftForward={canShiftForward}
          shiftDate={shiftDate}
          periods={PERIODS}
        />
      </div>
    </div>
  )
}

export default DashboardAnalytics
